/** The thread id of a reviewer comment: its round and its place in that round's comments. */
export const commentThreadId = (round, n) => `c:${round}.${n}`;
/** The thread id of a field-change card. */
export const cardThreadId = (cardKey) => `fc:${cardKey}`;
/** The note's threads from a stored body (anything malformed is left out). */
export function readReviewThreads(v) {
    if (!Array.isArray(v))
        return [];
    return v
        .filter((t) => !!t &&
        typeof t === 'object' &&
        typeof t.id === 'string' &&
        typeof t.section === 'string')
        .map((t) => ({ ...t, replies: Array.isArray(t.replies) ? t.replies : [] }));
}
/**
 * The threads to store when one side saves: the stored ones, with the new replies (an empty `at`,
 * not already kept under the same id) stamped with this user and now, and each tick as sent. A
 * field-change card's thread is made on its first reply or tick; a comment's only as the note is
 * sent back (commentThreads). Nobody edits or removes a reply already kept.
 */
export function mergeReviewThreads(stored, sent, me) {
    const out = (stored ?? []).map((t) => ({ ...t, replies: [...t.replies] }));
    for (const s of sent ?? []) {
        let t = out.find((x) => x.id === s.id);
        const fresh = (s.replies ?? []).filter((r) => r.at === '' && r.text.trim() !== '' && !(r.id && t?.replies.some((k) => k.id === r.id)));
        const card = s.id.startsWith('fc:');
        // ehr#890 (point 4): the Added or Removed half of a list's card ("fc:<key>#add" / "#rem") on a
        // note saved with the one combined card reads that card's decision until it has its own, so
        // a half with the tick taken off is kept too (else it would read the old tick again)
        const whole = /#(add|rem)$/.test(s.id) ? s.id.replace(/#(add|rem)$/, '') : null;
        const offOld = whole !== null && out.some((x) => x.id === whole && !x.closed && (x.resolved || x.denied));
        const asks = fresh.length > 0 || s.resolved === true || s.denied === true || offOld;
        // ehr#741 (Chase 10-31, point 6): an own card closed with the red X keeps nothing (no
        // replies, no tick), so it never comes back; the next card on that field starts fresh
        if (card && s.closed === true) {
            if (t) {
                const { id, section } = t;
                const shut = { id, section, by: me.by, at: me.at, round: me.round, closed: true };
                out[out.indexOf(t)] = { ...shut, replies: [] };
            }
            continue;
        }
        if (t?.closed) {
            if (!asks)
                continue;
            out.splice(out.indexOf(t), 1);
            t = undefined;
        }
        if (!t) {
            if (!card || !asks)
                continue;
            t = { id: s.id, section: s.section, by: me.by, at: me.at, round: me.round, replies: [] };
            out.push(t);
        }
        for (const r of fresh.slice(0, Math.max(0, 50 - t.replies.length)))
            t.replies.push({ ...(r.id ? { id: r.id } : {}), by: me.by, at: me.at, text: r.text.trim() });
        // ehr#741 (Chase 10-31, point 8): kept (green) or not kept (red), never both
        if (s.resolved === true) {
            t.resolved = true;
            delete t.denied;
        }
        else if (s.denied === true && t.id.startsWith('fc:')) {
            t.denied = true;
            delete t.resolved;
        }
        else {
            delete t.resolved;
            delete t.denied;
        }
    }
    return out;
}
/** The comments sent with Request changes, as threads (each one's id names its round and place). */
export function commentThreads(suggestions) {
    if (!suggestions)
        return [];
    return suggestions.comments.map((c, n) => ({
        id: commentThreadId(suggestions.round, n),
        section: c.section,
        text: c.text,
        ...(c.quote ? { quote: c.quote, ...(c.pos !== undefined ? { pos: c.pos } : {}) } : {}),
        by: suggestions.by,
        at: suggestions.at,
        round: suggestions.round,
        replies: [],
    }));
}
/** Stored threads with a new round's comment threads added (one with the same id is replaced). */
export function withCommentThreads(stored, added) {
    const ids = new Set(added.map((t) => t.id));
    return [...stored.filter((t) => !ids.has(t.id)), ...added];
}
//# sourceMappingURL=reviewThreads.js.map